/**
 * CompendiumPanel — In-game reference browser.
 * Displays races, classes, enemies, items, and skill trees.
 * Opened with the K key (after inventory).
 *
 * Uses UIPanel base class for focus management and keyboard nav.
 */

import UIPanel from './UIPanel.js';
import DebugLog from '../debug/DebugLog.js';

// Lazy data imports — loaded on first open to avoid startup cost
let _races       = null;
let _classes     = null;
let _enemies     = null;
let _items       = null;
let _skillTrees  = null;

async function loadData() {
  if (_races) return;
  const [racesMod, classesMod, enemiesMod, itemsMod, skillTreesMod] = await Promise.all([
    import('../data/races.json',       { assert: { type: 'json' } }).catch(() => ({ default: [] })),
    import('../data/classes.json',     { assert: { type: 'json' } }).catch(() => ({ default: [] })),
    import('../data/enemies.json',     { assert: { type: 'json' } }).catch(() => ({ default: [] })),
    import('../data/items.json',       { assert: { type: 'json' } }).catch(() => ({ default: [] })),
    import('../data/skill_trees.json', { assert: { type: 'json' } }).catch(() => ({ default: [] })),
  ]);
  _races      = racesMod.default;
  _classes    = classesMod.default;
  _enemies    = enemiesMod.default;
  _items      = itemsMod.default;
  _skillTrees = skillTreesMod.default;
}

const TABS = [
  { id: 'races',       label: 'Races'       },
  { id: 'classes',     label: 'Classes'     },
  { id: 'enemies',     label: 'Enemies'     },
  { id: 'items',       label: 'Items'       },
  { id: 'skill_trees', label: 'Skill Trees' },
];

class CompendiumPanel extends UIPanel {
  constructor() {
    super('panel-compendium', {
      classes: ['spellroads-panel', 'compendium-panel'],
      layer: 'panel',
    });
    this._activeTab = 'races';
    this._searchQuery = '';
  }

  async open() {
    if (this.visible) { this.hide(); return; }

    await loadData();
    this._build();
    this.show();
    DebugLog.info('[compendium] opened');
  }

  toggle() {
    if (this.visible) { this.hide(); } else { this.open(); }
  }

  _build() {
    this.el.innerHTML = '';
    this._injectStyles();

    const inner = document.createElement('div');
    inner.className = 'compendium-inner';

    // Header
    const header = document.createElement('div');
    header.className = 'compendium-header';

    const title = document.createElement('h2');
    title.className = 'compendium-title';
    title.textContent = 'Compendium';

    const closeBtn = document.createElement('button');
    closeBtn.className = 'spellroads-btn compendium-close';
    closeBtn.textContent = '✕';
    closeBtn.setAttribute('aria-label', 'Close compendium');
    closeBtn.addEventListener('click', () => this.hide());

    header.appendChild(title);
    header.appendChild(closeBtn);

    // Search
    const searchRow = document.createElement('div');
    searchRow.className = 'compendium-search-row';
    const searchInput = document.createElement('input');
    searchInput.type = 'text';
    searchInput.className = 'compendium-search';
    searchInput.placeholder = 'Search\u2026';
    searchInput.value = this._searchQuery;
    searchInput.addEventListener('input', (e) => {
      this._searchQuery = e.target.value.toLowerCase();
      this._renderContent(content);
    });
    searchRow.appendChild(searchInput);

    // Tab bar
    const tabBar = document.createElement('div');
    tabBar.className = 'compendium-tabs';
    tabBar.setAttribute('role', 'tablist');

    for (const tab of TABS) {
      const btn = document.createElement('button');
      btn.className = 'compendium-tab' + (tab.id === this._activeTab ? ' active' : '');
      btn.textContent = tab.label;
      btn.dataset.tab = tab.id;
      btn.setAttribute('role', 'tab');
      btn.setAttribute('aria-selected', String(tab.id === this._activeTab));
      btn.addEventListener('click', () => {
        this._activeTab = tab.id;
        this._searchQuery = '';
        searchInput.value = '';
        tabBar.querySelectorAll('.compendium-tab').forEach(b => {
          b.classList.toggle('active', b.dataset.tab === tab.id);
          b.setAttribute('aria-selected', String(b.dataset.tab === tab.id));
        });
        this._renderContent(content);
      });
      tabBar.appendChild(btn);
    }

    // Content area
    const content = document.createElement('div');
    content.className = 'compendium-content';

    inner.appendChild(header);
    inner.appendChild(searchRow);
    inner.appendChild(tabBar);
    inner.appendChild(content);
    this.el.appendChild(inner);

    this._renderContent(content);
  }

  _renderContent(container) {
    container.innerHTML = '';
    const query = this._searchQuery;

    let items = [];
    switch (this._activeTab) {
      case 'races':       items = (_races ?? []).map(r => this._raceCard(r));       break;
      case 'classes':     items = (_classes ?? []).map(c => this._classCard(c));     break;
      case 'enemies':     items = (_enemies ?? []).map(e => this._enemyCard(e));     break;
      case 'items':       items = (_items ?? []).map(i => this._itemCard(i));         break;
      case 'skill_trees': items = (_skillTrees ?? []).map(t => this._treeCard(t));  break;
    }

    // Filter by search query
    if (query) {
      items = items.filter(el => el.dataset.search?.includes(query));
    }

    if (items.length === 0) {
      const empty = document.createElement('p');
      empty.className = 'compendium-empty';
      empty.textContent = query ? `No results for "${query}"` : 'No entries found.';
      container.appendChild(empty);
      return;
    }

    const grid = document.createElement('div');
    grid.className = 'compendium-grid';
    items.forEach(card => grid.appendChild(card));
    container.appendChild(grid);
  }

  _raceCard(race) {
    const el = document.createElement('div');
    el.className = 'comp-card';
    el.dataset.search = (race.name + ' ' + race.description).toLowerCase();
    el.innerHTML = `
      <div class="comp-card-name">${race.name}</div>
      <div class="comp-card-sub">${race.homeRegion ?? ''}</div>
      <div class="comp-card-desc">${race.description ?? ''}</div>
    `;
    return el;
  }

  _classCard(cls) {
    const el = document.createElement('div');
    el.className = 'comp-card';
    el.dataset.search = (cls.name + ' ' + (cls.description ?? '')).toLowerCase();
    el.innerHTML = `
      <div class="comp-card-name">${cls.name}</div>
      <div class="comp-card-sub">${cls.archetype ?? ''}</div>
      <div class="comp-card-desc">${cls.description ?? ''}</div>
    `;
    return el;
  }

  _enemyCard(enemy) {
    const el = document.createElement('div');
    el.className = 'comp-card';
    el.dataset.search = (enemy.name + ' ' + (enemy.description ?? '') + ' ' + (enemy.type ?? '')).toLowerCase();
    el.innerHTML = `
      <div class="comp-card-name">${enemy.name}</div>
      <div class="comp-card-sub">Level ${enemy.level ?? '?'} · ${enemy.type ?? ''}</div>
      <div class="comp-card-desc">${enemy.description ?? ''}</div>
    `;
    return el;
  }

  _itemCard(item) {
    const el = document.createElement('div');
    el.className = 'comp-card';
    el.dataset.search = (item.name + ' ' + (item.description ?? '') + ' ' + (item.type ?? '')).toLowerCase();
    const dmg = item.damage ? `${item.damage.min}–${item.damage.max} dmg` : '';
    const def = item.defense ? `${item.defense} def` : '';
    el.innerHTML = `
      <div class="comp-card-name">${item.name}</div>
      <div class="comp-card-sub">${item.type ?? ''} · ${item.rarity ?? ''} ${dmg || def ? '· ' + [dmg, def].filter(Boolean).join(', ') : ''}</div>
      <div class="comp-card-desc">${item.description ?? ''}</div>
    `;
    return el;
  }

  _treeCard(tree) {
    const el = document.createElement('div');
    el.className = 'comp-card';
    el.dataset.search = (tree.name + ' ' + (tree.description ?? '')).toLowerCase();
    const skillCount = Array.isArray(tree.skills) ? tree.skills.length : 0;
    el.innerHTML = `
      <div class="comp-card-name">${tree.name}</div>
      <div class="comp-card-sub">${skillCount} skills · ${tree.archetype ?? ''}</div>
      <div class="comp-card-desc">${tree.description ?? ''}</div>
    `;
    return el;
  }

  _injectStyles() {
    const STYLE_ID = 'spellroads-compendium-style';
    if (document.getElementById(STYLE_ID)) return;
    const style = document.createElement('style');
    style.id = STYLE_ID;
    style.textContent = `
      #panel-compendium {
        position: fixed;
        inset: 0;
        z-index: 250;
        display: none;
        align-items: center;
        justify-content: center;
        background: rgba(0, 0, 0, 0.55);
        pointer-events: none;
      }
      #panel-compendium.visible {
        display: flex;
        pointer-events: all;
      }
      .compendium-inner {
        width: min(820px, 95vw);
        max-height: 88vh;
        background: #1a1510;
        border: 1px solid rgba(200, 160, 32, 0.3);
        border-radius: 6px;
        display: flex;
        flex-direction: column;
        overflow: hidden;
        box-shadow: 0 8px 40px rgba(0,0,0,0.8);
        font-family: 'Segoe UI', system-ui, sans-serif;
      }
      .compendium-header {
        display: flex;
        align-items: center;
        justify-content: space-between;
        padding: 14px 18px 10px;
        border-bottom: 1px solid rgba(200, 160, 32, 0.15);
      }
      .compendium-title {
        color: #c8a020;
        font-size: clamp(1rem, 2.5vw, 1.3rem);
        letter-spacing: 0.1em;
        font-weight: 700;
      }
      .compendium-close {
        background: none;
        border: 1px solid rgba(200, 160, 32, 0.2);
        color: #8a7a5a;
        padding: 4px 10px;
        border-radius: 3px;
        cursor: pointer;
        font-size: 0.9rem;
        min-height: 30px;
      }
      .compendium-close:hover { border-color: rgba(200, 160, 32, 0.5); color: #c8a020; }
      .compendium-search-row {
        padding: 8px 18px;
        border-bottom: 1px solid rgba(200, 160, 32, 0.1);
      }
      .compendium-search {
        width: 100%;
        background: rgba(200, 160, 32, 0.05);
        border: 1px solid rgba(200, 160, 32, 0.2);
        border-radius: 3px;
        color: #e8d5b0;
        font-family: inherit;
        font-size: clamp(0.8rem, 1.5vw, 0.9rem);
        padding: 6px 10px;
        outline: none;
      }
      .compendium-search:focus { border-color: rgba(200, 160, 32, 0.5); }
      .compendium-tabs {
        display: flex;
        border-bottom: 1px solid rgba(200, 160, 32, 0.15);
        padding: 0 18px;
        gap: 2px;
        overflow-x: auto;
      }
      .compendium-tab {
        background: none;
        border: none;
        border-bottom: 2px solid transparent;
        color: #7a6a50;
        font-family: inherit;
        font-size: clamp(0.75rem, 1.5vw, 0.88rem);
        padding: 8px 14px;
        cursor: pointer;
        letter-spacing: 0.05em;
        white-space: nowrap;
        transition: color 0.15s, border-color 0.15s;
        min-height: 36px;
      }
      .compendium-tab:hover { color: #c8a020; }
      .compendium-tab.active {
        color: #c8a020;
        border-bottom-color: #c8a020;
      }
      .compendium-content {
        flex: 1;
        overflow-y: auto;
        padding: 16px 18px;
      }
      .compendium-grid {
        display: grid;
        grid-template-columns: repeat(auto-fill, minmax(220px, 1fr));
        gap: 10px;
      }
      .comp-card {
        background: rgba(200, 160, 32, 0.04);
        border: 1px solid rgba(200, 160, 32, 0.15);
        border-radius: 4px;
        padding: 10px 12px;
        cursor: default;
        transition: border-color 0.15s, background 0.15s;
      }
      .comp-card:hover {
        background: rgba(200, 160, 32, 0.08);
        border-color: rgba(200, 160, 32, 0.3);
      }
      .comp-card-name {
        font-weight: 600;
        font-size: clamp(0.8rem, 1.5vw, 0.92rem);
        color: #e8d5b0;
        margin-bottom: 2px;
      }
      .comp-card-sub {
        font-size: clamp(0.68rem, 1.2vw, 0.78rem);
        color: #7a6a50;
        margin-bottom: 6px;
        letter-spacing: 0.03em;
      }
      .comp-card-desc {
        font-size: clamp(0.72rem, 1.3vw, 0.82rem);
        color: #a09070;
        line-height: 1.45;
      }
      .compendium-empty {
        color: #5a4a35;
        font-size: 0.9rem;
        padding: 20px;
        text-align: center;
      }
      @media (max-width: 767px) {
        .compendium-inner { max-height: 96vh; }
        .compendium-grid { grid-template-columns: 1fr; }
      }
    `;
    document.head.appendChild(style);
  }

  destroy() {
    this.hide();
    if (this.el?.parentNode) this.el.remove();
  }
}

export default CompendiumPanel;
